// =====================================================
// WKZRechner.jsx — Werbekostenzuschuss-Rechner
// Gerät Netto/Brutto · Tarife · Zusatzoptionen · Coupons
// Automatische WKZ-Berechnung mit ×1,19 MwSt
// =====================================================
import React, { useState, useEffect, useMemo, useCallback } from 'react';

function fmtEuro(v) { return (v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }); }

function GlassCard({ children, glow = 'cyan', style = {}, onClick }) {
  const glowMap = {
    cyan:   { border: 'rgba(56,189,248,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(56,189,248,0.08)' },
    green:  { border: 'rgba(0,245,212,0.18)',   bg: 'rgba(10,25,50,0.55)',  shadow: 'rgba(0,245,212,0.08)' },
    pink:   { border: 'rgba(255,102,162,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(255,102,162,0.08)' },
    purple: { border: 'rgba(167,139,250,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(167,139,250,0.08)' },
    orange: { border: 'rgba(251,146,60,0.18)',  bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(251,146,60,0.08)' },
    gold:   { border: 'rgba(255,215,0,0.18)',   bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(255,215,0,0.08)' },
  };
  const c = glowMap[glow] || glowMap.cyan;
  return (
    <div onClick={onClick} style={{ borderRadius: 20, border: `1px solid ${c.border}`, background: c.bg, backdropFilter: 'blur(24px) saturate(180%)', WebkitBackdropFilter: 'blur(24px) saturate(180%)', boxShadow: `0 8px 32px ${c.shadow}, inset 0 1px 0 rgba(255,255,255,0.06)`, padding: '18px 16px', ...style }}>
      {children}
    </div>
  );
}

function SectionLabel({ children, icon }) {
  return (
    <div style={{ fontSize: '0.68rem', fontWeight: 800, color: 'rgba(200,220,255,0.82)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
      {icon && <span>{icon}</span>}{children}
    </div>
  );
}

// ==================== COUPON SYSTEM ====================
const DEFAULT_COUPONS = [
  { id: 'cp-1', name: 'Neuvertrag Bonus', value: 50, type: 'fixed' },
  { id: 'cp-2', name: 'VVL Treuebonus', value: 30, type: 'fixed' },
  { id: 'cp-3', name: 'MNP Wechselbonus', value: 100, type: 'fixed' },
  { id: 'cp-4', name: 'Kombi-Vorteil Coupon', value: 25, type: 'fixed' },
  { id: 'cp-5', name: 'Samsung Trade-In', value: 200, type: 'fixed' },
  { id: 'cp-6', name: 'Apple Trade-In', value: 150, type: 'fixed' },
  { id: 'cp-7', name: 'Gerätezuschuss M', value: 50, type: 'fixed' },
  { id: 'cp-8', name: 'Gerätezuschuss L', value: 100, type: 'fixed' },
  { id: 'cp-9', name: 'Gerätezuschuss XL', value: 150, type: 'fixed' },
  { id: 'cp-10', name: 'Aktions-Coupon 10%', value: 10, type: 'percent' },
  { id: 'cp-11', name: 'Push-Aktion Premium', value: 75, type: 'fixed' },
  { id: 'cp-12', name: 'Push-Aktion Standard', value: 40, type: 'fixed' },
  { id: 'cp-13', name: 'Sonder-WKZ Quartal', value: 120, type: 'fixed' },
  { id: 'cp-14', name: 'Empfehlungs-Coupon', value: 50, type: 'fixed' },
  { id: 'cp-15', name: 'Event-Bonus', value: 60, type: 'fixed' },
];

export function WKZRechner() {
  // Data
  const [tariffs, setTariffs] = useState([]);
  const [zusatzoptionen, setZusatzoptionen] = useState([]);
  const [smartphones, setSmartphones] = useState([]);
  const [routers, setRouters] = useState([]);

  // Device
  const [priceMode, setPriceMode] = useState('brutto'); // 'netto' | 'brutto'
  const [devicePrice, setDevicePrice] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [deviceSearch, setDeviceSearch] = useState('');
  const [showDeviceSearch, setShowDeviceSearch] = useState(false);

  // Tariffs (bis zu 5)
  const [selectedTariffs, setSelectedTariffs] = useState([]);
  const [tariffSearch, setTariffSearch] = useState('');

  // Zusatzoptionen (bis zu 10)
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [optionSearch, setOptionSearch] = useState('');

  // Coupons (bis zu 15)
  const [coupons, setCoupons] = useState([]);
  const [couponSearch, setCouponSearch] = useState('');
  const [customCouponName, setCustomCouponName] = useState('');
  const [customCouponValue, setCustomCouponValue] = useState('');

  // Push-Aktionen
  const [pushBonuses, setPushBonuses] = useState([]);
  const [pushName, setPushName] = useState('');
  const [pushValue, setPushValue] = useState('');

  // Results
  const [calculated, setCalculated] = useState(false);

  // Load Data
  useEffect(() => {
    Promise.all([
      fetch('/api/tariffs').then(r => r.json()).catch(() => []),
      fetch('/api/options').then(r => r.json()).catch(() => []),
      fetch('/api/smartphones').then(r => r.json()).catch(() => []),
      fetch('/api/routers').then(r => r.json()).catch(() => []),
    ]).then(([t, z, s, rt]) => {
      setTariffs(t);
      setZusatzoptionen(z);
      setSmartphones(s.filter(d => d.active !== false));
      setRouters(rt.filter(d => d.active !== false));
    });
  }, []);

  // All devices combined for search
  const allDevices = useMemo(() => {
    const devs = [];
    smartphones.forEach(s => {
      if (s.storage && s.prices) {
        s.storage.forEach(st => {
          const label = st >= 1024 ? `${st/1024} TB` : `${st} GB`;
          devs.push({ id: `${s.id}-${st}`, name: `${s.name} ${label}`, brand: s.brand, price: s.prices[st] || 0, type: 'smartphone' });
        });
      } else {
        devs.push({ id: s.id, name: s.name, brand: s.brand, price: Object.values(s.prices || {})[0] || 0, type: 'smartphone' });
      }
    });
    routers.forEach(r => {
      devs.push({ id: r.id, name: r.name, brand: r.brand, price: r.purchasePrice || r.price || 0, type: 'router' });
    });
    return devs;
  }, [smartphones, routers]);

  // Filtered devices
  const filteredDevices = useMemo(() => {
    if (!deviceSearch.trim()) return allDevices.slice(0, 10);
    const q = deviceSearch.toLowerCase();
    return allDevices.filter(d => d.name.toLowerCase().includes(q) || d.brand.toLowerCase().includes(q)).slice(0, 10);
  }, [allDevices, deviceSearch]);

  // Filtered tariffs
  const filteredTariffs = useMemo(() => {
    if (!tariffSearch.trim()) return tariffs.slice(0, 15);
    const q = tariffSearch.toLowerCase();
    return tariffs.filter(t => t.title.toLowerCase().includes(q) || (t.category || '').toLowerCase().includes(q)).slice(0, 15);
  }, [tariffs, tariffSearch]);

  // Filtered options
  const filteredOptions = useMemo(() => {
    if (!optionSearch.trim()) return zusatzoptionen;
    const q = optionSearch.toLowerCase();
    return zusatzoptionen.filter(o => o.name.toLowerCase().includes(q));
  }, [zusatzoptionen, optionSearch]);

  // Filtered coupons for search
  const filteredCoupons = useMemo(() => {
    if (!couponSearch.trim()) return DEFAULT_COUPONS;
    const q = couponSearch.toLowerCase();
    return DEFAULT_COUPONS.filter(c => c.name.toLowerCase().includes(q));
  }, [couponSearch]);

  // Add/Remove Tariff
  const toggleTariff = useCallback((tariff) => {
    setSelectedTariffs(prev => {
      const exists = prev.find(t => t.id === tariff.id);
      if (exists) return prev.filter(t => t.id !== tariff.id);
      if (prev.length >= 5) return prev;
      return [...prev, { ...tariff, couponValue: 0 }];
    });
    setCalculated(false);
  }, []);

  // Add/Remove Option
  const toggleOption = useCallback((option) => {
    setSelectedOptions(prev => {
      const exists = prev.find(o => o.id === option.id);
      if (exists) return prev.filter(o => o.id !== option.id);
      if (prev.length >= 10) return prev;
      return [...prev, { ...option, couponValue: 0 }];
    });
    setCalculated(false);
  }, []);

  // Add Coupon
  const addCoupon = useCallback((coupon) => {
    setCoupons(prev => {
      if (prev.length >= 15) return prev;
      if (prev.find(c => c.id === coupon.id)) return prev;
      return [...prev, { ...coupon }];
    });
    setCalculated(false);
  }, []);

  // Add custom coupon
  const addCustomCoupon = useCallback(() => {
    if (!customCouponName.trim() || !customCouponValue) return;
    const id = 'custom-' + Date.now();
    setCoupons(prev => {
      if (prev.length >= 15) return prev;
      return [...prev, { id, name: customCouponName, value: parseFloat(customCouponValue) || 0, type: 'fixed' }];
    });
    setCustomCouponName('');
    setCustomCouponValue('');
    setCalculated(false);
  }, [customCouponName, customCouponValue]);

  // Remove coupon
  const removeCoupon = useCallback((id) => {
    setCoupons(prev => prev.filter(c => c.id !== id));
    setCalculated(false);
  }, []);

  // Add push bonus
  const addPush = useCallback(() => {
    if (!pushName.trim() || !pushValue) return;
    setPushBonuses(prev => {
      if (prev.length >= 15) return prev;
      return [...prev, { id: 'push-' + Date.now(), name: pushName, value: parseFloat(pushValue) || 0 }];
    });
    setPushName('');
    setPushValue('');
    setCalculated(false);
  }, [pushName, pushValue]);

  // Remove push
  const removePush = useCallback((id) => {
    setPushBonuses(prev => prev.filter(p => p.id !== id));
    setCalculated(false);
  }, []);

  // Select device
  const selectDevice = useCallback((device) => {
    setDeviceName(device.name);
    setDevicePrice(device.price.toString());
    setShowDeviceSearch(false);
    setDeviceSearch('');
    setCalculated(false);
  }, []);

  // ==================== CALCULATION ====================
  const calculation = useMemo(() => {
    const rawPrice = parseFloat(devicePrice) || 0;
    const nettoDevice = priceMode === 'brutto' ? rawPrice / 1.19 : rawPrice;
    const bruttoDevice = priceMode === 'netto' ? rawPrice * 1.19 : rawPrice;

    // Tariff monthly total (promoPrice or price)
    const tarifMonatlich = selectedTariffs.reduce((sum, t) => sum + (t.promoPrice || t.price || 0), 0);
    const tarifAnschluss = selectedTariffs.reduce((sum, t) => sum + (t.connectionFee || 0), 0);

    // Options monthly total
    const optionenMonatlich = selectedOptions.reduce((sum, o) => sum + (o.price || 0), 0);

    // Total monthly recurring
    const monatlichGesamt = tarifMonatlich + optionenMonatlich;

    // Coupon total (fixed)
    const couponFixed = coupons.filter(c => c.type === 'fixed').reduce((sum, c) => sum + c.value, 0);
    const couponPercent = coupons.filter(c => c.type === 'percent').reduce((sum, c) => sum + c.value, 0);
    const couponPercentValue = (nettoDevice * couponPercent) / 100;
    const couponGesamt = couponFixed + couponPercentValue;

    // Push total
    const pushGesamt = pushBonuses.reduce((sum, p) => sum + p.value, 0);

    // Gesamtbetrag aller Zuschüsse/Coupons (netto)
    const gesamtZuschuesse = couponGesamt + pushGesamt;

    // WKZ Berechnung: Zuschüsse × 1.19
    const wkzBrutto = gesamtZuschuesse * 1.19;

    // Effektiver Gerätepreis nach WKZ
    const effektivNetto = Math.max(0, nettoDevice - gesamtZuschuesse);
    const effektivBrutto = effektivNetto * 1.19;

    return {
      nettoDevice, bruttoDevice,
      tarifMonatlich, tarifAnschluss,
      optionenMonatlich,
      monatlichGesamt,
      couponGesamt, couponPercentValue,
      pushGesamt,
      gesamtZuschuesse,
      wkzBrutto,
      effektivNetto, effektivBrutto,
      selectedTariffCount: selectedTariffs.length,
      selectedOptionCount: selectedOptions.length,
      couponCount: coupons.length,
      pushCount: pushBonuses.length,
    };
  }, [devicePrice, priceMode, selectedTariffs, selectedOptions, coupons, pushBonuses]);

  // Reset all
  const resetAll = useCallback(() => {
    setDevicePrice(''); setDeviceName(''); setDeviceSearch('');
    setSelectedTariffs([]); setSelectedOptions([]);
    setCoupons([]); setPushBonuses([]);
    setCalculated(false);
  }, []);

  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: '0.78rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ minHeight: '100vh', color: '#fff', fontFamily: "'Poppins', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '12px 16px 100px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 900, background: 'linear-gradient(135deg, #ffd700, #fb923c, #ff66a2)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>💰 WKZ Rechner</div>
          <div style={{ fontSize: '0.68rem', color: 'rgba(200,220,255,0.72)', marginTop: 4 }}>Werbekostenzuschuss berechnen · Gerät + Tarife + Coupons</div>
        </div>

        {/* ========== 1. GERÄT ========== */}
        <GlassCard glow="gold" style={{ marginBottom: 14 }}>
          <SectionLabel icon="📱">1. Gerät auswählen</SectionLabel>

          {/* Netto/Brutto Toggle */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 12, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 3, border: '1px solid rgba(255,255,255,0.06)' }}>
            <button onClick={() => { setPriceMode('brutto'); setCalculated(false); }}
              style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: priceMode === 'brutto' ? 'rgba(255,215,0,0.15)' : 'transparent', color: priceMode === 'brutto' ? '#ffd700' : 'rgba(200,220,255,0.5)', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}>
              Brutto (inkl. MwSt)
            </button>
            <button onClick={() => { setPriceMode('netto'); setCalculated(false); }}
              style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: priceMode === 'netto' ? 'rgba(255,215,0,0.15)' : 'transparent', color: priceMode === 'netto' ? '#ffd700' : 'rgba(200,220,255,0.5)', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}>
              Netto (exkl. MwSt)
            </button>
          </div>

          {/* Device Name + Price */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: '0.52rem', fontWeight: 700, color: 'rgba(200,220,255,0.65)', marginBottom: 3, textTransform: 'uppercase' }}>Gerätename</div>
              <input value={deviceName} onChange={e => { setDeviceName(e.target.value); setCalculated(false); }} placeholder="z.B. iPhone 16 Pro 256GB" style={inputStyle} />
            </div>
            <div>
              <div style={{ fontSize: '0.52rem', fontWeight: 700, color: 'rgba(200,220,255,0.65)', marginBottom: 3, textTransform: 'uppercase' }}>Preis ({priceMode === 'brutto' ? 'Brutto' : 'Netto'}) €</div>
              <input type="number" value={devicePrice} onChange={e => { setDevicePrice(e.target.value); setCalculated(false); }} placeholder="0.00" style={inputStyle} />
            </div>
          </div>

          {/* Device Search */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowDeviceSearch(!showDeviceSearch)}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 10, border: '1px solid rgba(255,215,0,0.15)', background: 'rgba(255,215,0,0.04)', color: '#ffd700', fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
              🔍 Gerät aus Datenbank suchen...
            </button>
            {showDeviceSearch && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, marginTop: 4, background: 'rgba(10,20,50,0.97)', border: '1px solid rgba(255,215,0,0.2)', borderRadius: 14, padding: 10, maxHeight: 280, overflowY: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.5)' }}>
                <input value={deviceSearch} onChange={e => setDeviceSearch(e.target.value)} placeholder="Suche..." autoFocus
                  style={{ ...inputStyle, marginBottom: 8, border: '1px solid rgba(255,215,0,0.2)' }} />
                {filteredDevices.map(d => (
                  <button key={d.id} onClick={() => selectDevice(d)}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '8px 10px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.02)', color: '#fff', fontSize: '0.68rem', cursor: 'pointer', fontFamily: 'inherit', marginBottom: 3, textAlign: 'left' }}>
                    <span>{d.type === 'smartphone' ? '📱' : '📡'} {d.name}</span>
                    <span style={{ color: '#00f5d4', fontWeight: 800, fontSize: '0.72rem' }}>{fmtEuro(d.price)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Device Price Display */}
          {devicePrice && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <div style={{ flex: 1, padding: '8px 10px', borderRadius: 10, background: 'rgba(255,215,0,0.06)', border: '1px solid rgba(255,215,0,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Netto</div>
                <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#ffd700' }}>{fmtEuro(calculation.nettoDevice)}</div>
              </div>
              <div style={{ flex: 1, padding: '8px 10px', borderRadius: 10, background: 'rgba(0,245,212,0.06)', border: '1px solid rgba(0,245,212,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Brutto</div>
                <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#00f5d4' }}>{fmtEuro(calculation.bruttoDevice)}</div>
              </div>
            </div>
          )}
        </GlassCard>

        {/* ========== 2. TARIFE ========== */}
        <GlassCard glow="cyan" style={{ marginBottom: 14 }}>
          <SectionLabel icon="📶">2. Tarife auswählen (max. 5)</SectionLabel>
          <input value={tariffSearch} onChange={e => setTariffSearch(e.target.value)} placeholder="🔍 Tarif suchen..."
            style={{ ...inputStyle, marginBottom: 10, border: '1px solid rgba(56,189,248,0.2)' }} />

          {/* Selected */}
          {selectedTariffs.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              {selectedTariffs.map(t => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.2)', marginBottom: 4 }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#38bdf8', flex: 1 }}>{t.title}</span>
                  <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#00f5d4' }}>{fmtEuro(t.promoPrice || t.price)}/mtl.</span>
                  <button onClick={() => toggleTariff(t)} style={{ background: 'none', border: 'none', color: '#ff006e', cursor: 'pointer', fontSize: '0.82rem' }}>✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Tariff List */}
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {filteredTariffs.filter(t => !selectedTariffs.find(s => s.id === t.id)).slice(0, 10).map(t => (
              <button key={t.id} onClick={() => toggleTariff(t)} disabled={selectedTariffs.length >= 5}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.04)', background: 'rgba(255,255,255,0.02)', color: '#fff', fontSize: '0.65rem', cursor: selectedTariffs.length >= 5 ? 'not-allowed' : 'pointer', fontFamily: 'inherit', marginBottom: 3, textAlign: 'left', opacity: selectedTariffs.length >= 5 ? 0.4 : 1 }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{t.title}</div>
                  <div style={{ fontSize: '0.52rem', color: 'rgba(200,220,255,0.55)' }}>{t.category} · {t.dataLabel} GB</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {t.promoPrice && <div style={{ fontWeight: 800, color: '#00f5d4' }}>{fmtEuro(t.promoPrice)}/mtl.</div>}
                  {t.promoPrice && t.price && <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.5)', textDecoration: 'line-through' }}>{fmtEuro(t.price)}</div>}
                  {!t.promoPrice && <div style={{ fontWeight: 800, color: '#fff' }}>{fmtEuro(t.price)}/mtl.</div>}
                </div>
              </button>
            ))}
          </div>
        </GlassCard>

        {/* ========== 3. ZUSATZOPTIONEN ========== */}
        <GlassCard glow="purple" style={{ marginBottom: 14 }}>
          <SectionLabel icon="🛍️">3. Zusatzoptionen (max. 10)</SectionLabel>
          <input value={optionSearch} onChange={e => setOptionSearch(e.target.value)} placeholder="🔍 Option suchen..."
            style={{ ...inputStyle, marginBottom: 10, border: '1px solid rgba(167,139,250,0.2)' }} />

          {/* Selected */}
          {selectedOptions.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              {selectedOptions.map(o => (
                <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.2)', marginBottom: 4 }}>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#a78bfa', flex: 1 }}>{o.name}</span>
                  <span style={{ fontSize: '0.72rem', fontWeight: 800, color: o.price < 0 ? '#00f5d4' : '#fff' }}>{fmtEuro(o.price)}/mtl.</span>
                  <button onClick={() => toggleOption(o)} style={{ background: 'none', border: 'none', color: '#ff006e', cursor: 'pointer', fontSize: '0.82rem' }}>✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Options List */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {filteredOptions.filter(o => !selectedOptions.find(s => s.id === o.id)).map(o => (
              <button key={o.id} onClick={() => toggleOption(o)} disabled={selectedOptions.length >= 10}
                style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid rgba(167,139,250,0.15)', background: 'rgba(167,139,250,0.04)', color: 'rgba(200,220,255,0.82)', fontSize: '0.58rem', fontWeight: 700, cursor: selectedOptions.length >= 10 ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: selectedOptions.length >= 10 ? 0.4 : 1 }}>
                {o.name} · {fmtEuro(o.price)}
              </button>
            ))}
          </div>
        </GlassCard>

        {/* ========== 4. COUPONS ========== */}
        <GlassCard glow="green" style={{ marginBottom: 14 }}>
          <SectionLabel icon="🎟️">4. Coupons & Gutscheine (max. 15)</SectionLabel>
          <input value={couponSearch} onChange={e => setCouponSearch(e.target.value)} placeholder="🔍 Coupon suchen..."
            style={{ ...inputStyle, marginBottom: 10, border: '1px solid rgba(0,245,212,0.2)' }} />

          {/* Selected coupons */}
          {coupons.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              {coupons.map(c => (
                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, background: 'rgba(0,245,212,0.06)', border: '1px solid rgba(0,245,212,0.15)', marginBottom: 4 }}>
                  <span style={{ fontSize: '0.65rem', color: '#00f5d4', flex: 1, fontWeight: 700 }}>🎟️ {c.name}</span>
                  <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#00f5d4' }}>{c.type === 'percent' ? `${c.value}%` : fmtEuro(c.value)}</span>
                  <button onClick={() => removeCoupon(c.id)} style={{ background: 'none', border: 'none', color: '#ff006e', cursor: 'pointer', fontSize: '0.82rem' }}>✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Coupon search results */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
            {filteredCoupons.filter(c => !coupons.find(s => s.id === c.id)).slice(0, 8).map(c => (
              <button key={c.id} onClick={() => addCoupon(c)} disabled={coupons.length >= 15}
                style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid rgba(0,245,212,0.12)', background: 'rgba(0,245,212,0.04)', color: 'rgba(200,220,255,0.78)', fontSize: '0.55rem', fontWeight: 700, cursor: coupons.length >= 15 ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                {c.name} · {c.type === 'percent' ? `${c.value}%` : fmtEuro(c.value)}
              </button>
            ))}
          </div>

          {/* Custom Coupon */}
          <div style={{ fontSize: '0.52rem', fontWeight: 700, color: 'rgba(200,220,255,0.65)', marginBottom: 4, textTransform: 'uppercase' }}>Eigenen Coupon hinzufügen</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'end' }}>
            <input value={customCouponName} onChange={e => setCustomCouponName(e.target.value)} placeholder="Name"
              style={{ ...inputStyle, flex: 1 }} />
            <input type="number" value={customCouponValue} onChange={e => setCustomCouponValue(e.target.value)} placeholder="Wert €"
              style={{ ...inputStyle, width: 100 }} />
            <button onClick={addCustomCoupon}
              style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #00f5d4, #0ea5e9)', color: '#fff', fontSize: '0.72rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              ➕
            </button>
          </div>
        </GlassCard>

        {/* ========== 5. PUSH-AKTIONEN ========== */}
        <GlassCard glow="orange" style={{ marginBottom: 14 }}>
          <SectionLabel icon="🚀">5. Push-Aktionen & Boni</SectionLabel>

          {/* Selected pushes */}
          {pushBonuses.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              {pushBonuses.map(p => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, background: 'rgba(251,146,60,0.06)', border: '1px solid rgba(251,146,60,0.15)', marginBottom: 4 }}>
                  <span style={{ fontSize: '0.65rem', color: '#fb923c', flex: 1, fontWeight: 700 }}>🚀 {p.name}</span>
                  <span style={{ fontSize: '0.72rem', fontWeight: 800, color: '#fb923c' }}>{fmtEuro(p.value)}</span>
                  <button onClick={() => removePush(p.id)} style={{ background: 'none', border: 'none', color: '#ff006e', cursor: 'pointer', fontSize: '0.82rem' }}>✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Add Push */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'end' }}>
            <input value={pushName} onChange={e => setPushName(e.target.value)} placeholder="Push-Name"
              style={{ ...inputStyle, flex: 1 }} />
            <input type="number" value={pushValue} onChange={e => setPushValue(e.target.value)} placeholder="Wert €"
              style={{ ...inputStyle, width: 100 }} />
            <button onClick={addPush}
              style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #fb923c, #f97316)', color: '#fff', fontSize: '0.72rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              ➕
            </button>
          </div>
        </GlassCard>

        {/* ========== BERECHNEN BUTTON ========== */}
        <button onClick={() => setCalculated(true)}
          style={{ width: '100%', padding: '16px', borderRadius: 16, border: 'none', background: 'linear-gradient(135deg, #ffd700, #fb923c, #ff66a2)', color: '#fff', fontSize: '1rem', fontWeight: 900, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 14, boxShadow: '0 8px 32px rgba(255,215,0,0.3)', transition: 'transform 0.2s', letterSpacing: '0.02em' }}>
          💰 WKZ Berechnen (×1,19)
        </button>

        {/* ========== ERGEBNISSE ========== */}
        {calculated && (
          <GlassCard glow="gold" style={{ marginBottom: 14, background: 'linear-gradient(135deg, rgba(255,215,0,0.06), rgba(10,25,50,0.55))' }}>
            <SectionLabel icon="📊">WKZ Ergebnis</SectionLabel>

            {/* Device Summary */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
              <div style={{ padding: '12px', borderRadius: 12, background: 'rgba(255,215,0,0.06)', border: '1px solid rgba(255,215,0,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Gerät Netto</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#ffd700' }}>{fmtEuro(calculation.nettoDevice)}</div>
              </div>
              <div style={{ padding: '12px', borderRadius: 12, background: 'rgba(0,245,212,0.06)', border: '1px solid rgba(0,245,212,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Gerät Brutto</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#00f5d4' }}>{fmtEuro(calculation.bruttoDevice)}</div>
              </div>
            </div>

            {/* Tariff + Options Summary */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
              <div style={{ padding: '10px', borderRadius: 12, background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Tarife/mtl.</div>
                <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#38bdf8' }}>{fmtEuro(calculation.tarifMonatlich)}</div>
                <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.5)' }}>{calculation.selectedTariffCount} Tarif(e)</div>
              </div>
              <div style={{ padding: '10px', borderRadius: 12, background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Optionen/mtl.</div>
                <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#a78bfa' }}>{fmtEuro(calculation.optionenMonatlich)}</div>
                <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.5)' }}>{calculation.selectedOptionCount} Option(en)</div>
              </div>
              <div style={{ padding: '10px', borderRadius: 12, background: 'rgba(255,102,162,0.06)', border: '1px solid rgba(255,102,162,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Gesamt/mtl.</div>
                <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#ff66a2' }}>{fmtEuro(calculation.monatlichGesamt)}</div>
                <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.5)' }}>Anschluss: {fmtEuro(calculation.tarifAnschluss)}</div>
              </div>
            </div>

            {/* Coupon + Push Summary */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
              <div style={{ padding: '12px', borderRadius: 12, background: 'rgba(0,245,212,0.06)', border: '1px solid rgba(0,245,212,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Coupons gesamt</div>
                <div style={{ fontSize: '1rem', fontWeight: 900, color: '#00f5d4' }}>{fmtEuro(calculation.couponGesamt)}</div>
                <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.5)' }}>{calculation.couponCount} Coupon(s)</div>
              </div>
              <div style={{ padding: '12px', borderRadius: 12, background: 'rgba(251,146,60,0.06)', border: '1px solid rgba(251,146,60,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Push-Boni gesamt</div>
                <div style={{ fontSize: '1rem', fontWeight: 900, color: '#fb923c' }}>{fmtEuro(calculation.pushGesamt)}</div>
                <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.5)' }}>{calculation.pushCount} Push(es)</div>
              </div>
            </div>

            {/* ====== WKZ TOTAL ====== */}
            <div style={{ padding: '18px', borderRadius: 16, background: 'linear-gradient(135deg, rgba(255,215,0,0.1), rgba(251,146,60,0.08))', border: '2px solid rgba(255,215,0,0.25)', textAlign: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(200,220,255,0.7)', textTransform: 'uppercase', marginBottom: 4 }}>Gesamtzuschüsse (Netto)</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#ffd700' }}>{fmtEuro(calculation.gesamtZuschuesse)}</div>
              <div style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.6)', marginTop: 4 }}>
                × 1,19 MwSt = <strong style={{ color: '#00f5d4', fontSize: '0.78rem' }}>{fmtEuro(calculation.wkzBrutto)}</strong> Brutto
              </div>
            </div>

            {/* Effective Price */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div style={{ padding: '14px', borderRadius: 14, background: 'rgba(0,245,212,0.08)', border: '1px solid rgba(0,245,212,0.2)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Effektiv Netto</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 900, color: '#00f5d4' }}>{fmtEuro(calculation.effektivNetto)}</div>
                <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.5)' }}>nach Abzug WKZ</div>
              </div>
              <div style={{ padding: '14px', borderRadius: 14, background: 'rgba(255,102,162,0.08)', border: '1px solid rgba(255,102,162,0.2)', textAlign: 'center' }}>
                <div style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Effektiv Brutto</div>
                <div style={{ fontSize: '1.3rem', fontWeight: 900, color: '#ff66a2' }}>{fmtEuro(calculation.effektivBrutto)}</div>
                <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.5)' }}>nach Abzug WKZ</div>
              </div>
            </div>
          </GlassCard>
        )}

        {/* Reset */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={resetAll}
            style={{ flex: 1, padding: '12px', borderRadius: 12, border: '1px solid rgba(255,0,110,0.2)', background: 'rgba(255,0,110,0.06)', color: '#ff006e', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            🗑️ Alles zurücksetzen
          </button>
        </div>
      </div>
    </div>
  );
}

export default WKZRechner;
